import React from "react";

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Box,
  Flex,
  IconButton,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Progress,
  Select,
  Table,
  TableContainer,
  Tbody,
  Tr,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
} from "@chakra-ui/react";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import NumberField from "../NumberField/NumberField";
import { AiOutlineArrowLeft, AiOutlineArrowRight, AiOutlineDoubleLeft, AiOutlineDoubleRight } from "react-icons/ai";
import { showErrorToast, SORT_DIRECTIONS } from "../../utils";
import { useToast } from "@chakra-ui/react";
import { cellStyle } from "./cellStyle";

/**
 * This is a new version of DataTable. It will be a replacement for DataTable.
 * Differences: minimal for now, but will be expanded to include more features.
 *
 * Note: Keeping the old DataTable for now means we can be more free in refactoring here.
 *
 * <p>
 * Rows can be coloured etc using:
 *  dataItem.rowCustomStyles
 * Which is not actually styles but jsx/chakra props
 *
 * @param {Object} props
 * @param {Object[]} props.data the data behind the rows -- this gets unpicked by the columns
 * @param {ColumnDef[]} props.columns Tanstack Table ColumnDefs See https://tanstack.com/table/latest/docs/guide/column-defs
 * @param {boolean} props.isLoading
 * @param {boolean} props.serverSidePagination - Whether the data is paginated on the server side.
 * @param {function?} props.onRowClick Called with (event, row)
 * @param {number} props.queryPageCount - The total number of pages.
 * @param {number} props.queryTotalCount - The total number of rows.
 * @param {number} props.queryPageIndex - The current page index.
 * @param {function} props.setQueryPageIndex - The function to set the current page index.
 * @param {number} props.queryPageSize - The number of rows per page.
 * @param {function} props.setQueryPageSize - The function to set the number of rows per page.
 */
function DataTableV2({
  data,
  columns,
  isLoading,
  serverSidePagination = false,
  queryPageCount,
  queryTotalCount,
  queryPageIndex = 0,
  setQueryPageIndex,
  queryPageSize = 20,
  setQueryPageSize,
  queryPageSortBy = [],
  setQueryPageSortBy, // TODO document this
  containerHeight,
  enableFilters = false,
  enableSorting = false,
  showPaginator = true,
  onRowClick,
  size = "md",
  maxHeight,
  emptyMessage,
  ...rest
}) {
  const { t } = useTranslation("translation", {
    keyPrefix: "dataTable",
  });
  // HACK: mark custom cell render functions so we can check for them later
  columns
    .filter((c) => c.cell)
    .forEach((c) => {
      c.isCustomRender = true;
    });

  // TanStack react-Table
  const table = useReactTable({
    data,
    columns,
    // use Tanstack defaults
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    initialState: {
      pagination: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy, // How does sorting work?? Why doesnt it just work??
      },
    },
    pageCount: queryPageCount,

    manualSorting: serverSidePagination,
    manualFiltering: serverSidePagination,
    manualPagination: serverSidePagination,

    autoResetSortBy: false,
    autoResetFilters: false,

    enableFilters: enableFilters,
    enableSorting: enableSorting,
    // defaultColumn
  });


  // const rowModel = table.getSortedRowModel();
  const rowModel = table && data && data.length > 0 ? table.getSortedRowModel() : { rows: [] };

  return (
    <Box
      pos="relative"
      borderWidth="thin"
      borderRadius="md"
      overflow="hidden"
      height={containerHeight}
      backgroundColor="white"
      {...rest}
    >
      {isLoading && (
        <Progress
          size="xs"
          isIndeterminate
          pos="absolute"
          insetInline="0"
          insetBlockStart="0"
          zIndex="3"
          colorScheme="customScheme"
        />
      )}
      <TableContainer
        overflowY="hidden"
      >
        <Table size={size}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <RenderHeaderRow
                key={headerGroup.id}
                headerGroup={headerGroup}
                setQueryPageSortBy={setQueryPageSortBy}
                serverSidePagination={serverSidePagination}
              />
            ))}
          </Thead>
        </Table>
      </TableContainer>
      <Box
        overflowY="auto"
        maxHeight={maxHeight}
        sx={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
          "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
          scrollbarWidth: "thin",
          scrollbarColor: "transparent transparent",
          "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
        }}
      >
        <Table size={size}>
          <Tbody>
            {rowModel.rows.length === 0 && (
              <Tr>
                <Td colSpan={999} textAlign="center">
                  {emptyMessage || t("noDataMessage")}
                </Td>
              </Tr>
            )}
            {rowModel.rows.map((row) => (
              <RenderRow key={row.id || JSON.stringify(row)} row={row} onRowClick={onRowClick} />
            ))}
          </Tbody>
        </Table>
      </Box>
      {rowModel.rows.length > 0 && showPaginator && (
        <Paginator
          table={table}
          setQueryPageIndex={setQueryPageIndex}
          setQueryPageSize={setQueryPageSize}
          t={t}
          serverSidePagination={serverSidePagination}
          queryTotalCount={queryTotalCount}
        />
      )}
    </Box>
  );
}

function RenderRow({ row, onRowClick }) {
  const rowCustomStyles = row.original.rowCustomStyles ?? {};

  return (
    <Tr key={row.id} {...rowCustomStyles} onClick={(e) => onRowClick && onRowClick(e, row)}>
      {row.getVisibleCells().map((cell) => (
        <RenderCell key={cell.id} cell={cell} />
      ))}
    </Tr>
  );
}

function RenderCell({ cell }) {
  const isNumeric = !isNaN(cell.getValue());
  if (cell.getValue() === null || typeof cell.getValue() === "undefined") {
    cell.value = "";
  }

  const styleObj = cellStyle(cell);

  let cellRender = cell.column.columnDef.cell;
  // number rendering?
  if (isNumeric) {
    if (!cell?.column?.columnDef?.skipNumericCheck && !cell?.column?.isCustomRender) {
      cellRender = <NumberField value={cell.getValue()} align={"left"} />;
    }
  }

  return (
    <Td whiteSpace="pre-wrap" style={styleObj}>
      {flexRender(cellRender, cell.getContext())}
    </Td>
  );
}

/**
 * Apply sorting to the column.
 * @param {Object} props
 * @param {MouseEvent} props.event Needed for client-side sorting
 * @param {Column} props.column
 * @param {function} props.setQueryPageSortBy
 * @param {boolean} props.serverSidePagination
 */
function handleColumnHeaderClick({ event, column, setQueryPageSortBy, serverSidePagination }) {
  if (!serverSidePagination) {
    const handler = column.getToggleSortingHandler();
    handler(event);
    return;
  }
  let newSortDirection;
  switch (column.columnDef.sortDirection) {
    case SORT_DIRECTIONS.NONE:
      newSortDirection = SORT_DIRECTIONS.ASC;
      break;
    case SORT_DIRECTIONS.ASC:
      newSortDirection = SORT_DIRECTIONS.DESC;
      break;
    case SORT_DIRECTIONS.DESC:
      newSortDirection = SORT_DIRECTIONS.NONE;
      break;
    default:
      newSortDirection = SORT_DIRECTIONS.NONE;
  }

  if (setQueryPageSortBy) {
    setQueryPageSortBy([`${column.id},${newSortDirection}`]);
  }
}

function RenderHeaderRow({ headerGroup, setQueryPageSortBy, serverSidePagination }) {
  return (
    <Tr key={headerGroup.id}>
      {headerGroup.headers.map((header) => (
        <RenderHeaderCell
          key={header.id}
          header={header}
          setQueryPageSortBy={setQueryPageSortBy}
          serverSidePagination={serverSidePagination}
        />
      ))}
    </Tr>
  );
}

/**
 *
 * @param {Object} previousPage
 * @param {import("@tanstack/react-table").Header} props.header
 * @param {string} props.bg
 * @param {string} props.shadowColor
 * @param {function} props.setQueryPageSortBy
 */
function RenderHeaderCell({ header, setQueryPageSortBy, serverSidePagination }) {
  const column = header.column;
  const styleObj = {
    verticalAlign: "top",
    paddingInline: "var(--chakra-space-4)",
  };

  if (header.getSize()) {
    styleObj["minWidth"] = header.getSize();
    styleObj["maxWidth"] = header.getSize();
  }

  if (column.columnDef.customStyles && Object.keys(column.columnDef.customStyles)?.length > 0) {
    Object.keys(column.columnDef.customStyles).forEach((attr) => {
      styleObj[attr] = column.columnDef.customStyles[attr];
    });
  }

  return (
    <Th
      key={header.id}
      style={styleObj}
      pos="sticky"
      insetBlockStart="0"
      zIndex={1}
      borderBlockEndWidth="thin"
      userSelect="none"
      cursor="default !important"
      fontSize="xs"
      fontWeight="bold"
      color="gray.600"
      textTransform="uppercase"
      letterSpacing="normal"
    >
      {column.getCanSort() && (typeof column.columnDef.isSortable === "undefined" || column.columnDef.isSortable) ? (
        <Flex
          align="center"
          onClick={(event) => handleColumnHeaderClick({ event, column, setQueryPageSortBy, serverSidePagination })}
          cursor="pointer"
        >
          <Box marginRight={1}>{flexRender(column.columnDef.header, header.getContext())}</Box>
          <Box>
            {column.columnDef.sortDirection === SORT_DIRECTIONS.ASC ? (
              <FaSortUp />
            ) : column.columnDef.sortDirection === SORT_DIRECTIONS.DESC ? (
              <FaSortDown />
            ) : (
              <FaSort />
            )}
          </Box>
        </Flex>
      ) : (
        <Box>{flexRender(column.columnDef.header, header.getContext())}</Box>
      )}
    </Th>
  );
}

function Paginator({ table, setQueryPageIndex, setQueryPageSize, t, serverSidePagination, queryTotalCount }) {
  const toast = useToast();
  const nonIntegerSymbols = ["e", "E", ",", ".", "-", "+"];

  return (
    <Flex justifyContent="space-between" m={4} alignItems="center" overflowX="auto">
      <Flex>
        <Tooltip label={t("firstPageTooltip")}>
          <IconButton
            aria-label="first-page-button"
            onClick={() => {
              table.setPageIndex(0);
              if (setQueryPageIndex) setQueryPageIndex(0);
              if (setQueryPageSize) setQueryPageSize(table.getState().pagination.pageSize);
            }}
            isDisabled={!table.getCanPreviousPage()}
            icon={<AiOutlineDoubleLeft h={3} w={3} />}
            mr={4}
          />
        </Tooltip>
        <Tooltip label={t("prevPageTooltip")}>
          <IconButton
            aria-label="previous-page-button"
            onClick={() => {
              table.previousPage();
              if (setQueryPageIndex) setQueryPageIndex(table.getState().pagination.pageIndex - 1);
              if (setQueryPageSize) setQueryPageSize(table.getState().pagination.pageSize);
            }}
            isDisabled={!table.getCanPreviousPage()}
            icon={<AiOutlineArrowLeft h={6} w={6} />}
          />
        </Tooltip>
      </Flex>

      <Flex alignItems="center">
        <Text flexShrink="0" mr={8}>
          {`${t("page")} `}
          <Text fontWeight="bold" as="span">
            {table.getState().pagination.pageIndex + 1}
          </Text>
          {` ${t("of")} `}
          <Text fontWeight="bold" as="span">
            {table.getPageCount()}
          </Text>
        </Text>
        <Text flexShrink="0">{`${t("goToPage")}: `}</Text>
        <NumberInput
          ml={2}
          mr={8}
          w={28}
          min={1}
          max={table.getPageCount()}
          clampValueOnBlur={false}
          onKeyDown={(event) => nonIntegerSymbols.includes(event.key) && event.preventDefault()}
          onChange={(value) => {
            if (value !== "") {
              if (value < 1 || value > table.getPageCount()) {
                showErrorToast(toast, t("invalidPageMessage"));
              } else {
                toast.closeAll();
                const page = value ? value - 1 : 0;
                table.setPageIndex(page);
                if (setQueryPageIndex) setQueryPageIndex(page);
                if (setQueryPageSize) setQueryPageSize(table.getState().pagination.pageSize);
              }
            }
          }}
          defaultValue={table.getState().pagination.pageIndex + 1}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <Select
          w={32}
          value={table.getState().pagination.pageSize}
          onChange={(e) => {
            table.setPageSize(Number(e.target.value));
            if (setQueryPageSize) setQueryPageSize(Number(e.target.value));
          }}
          title={`${t("show")} ${table.getState().pagination.pageSize}`}
        >
          {[10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              {`${t("show")} ${pageSize}`}
            </option>
          ))}
        </Select>
        <Text flexShrink="0" mr={8} ml={4}>
          {t("totalCount")}:{" "}
          <Text fontWeight="bold" as="span">
            {serverSidePagination ? queryTotalCount : table.getFilteredRowModel().rows.length}
          </Text>
        </Text>
      </Flex>

      <Flex>
        <Tooltip label={t("nextPageTooltip")}>
          <IconButton
            aria-label="next-page-button"
            onClick={() => {
              table.nextPage();
              if (setQueryPageIndex) setQueryPageIndex(table.getState().pagination.pageIndex + 1);
              if (setQueryPageSize) setQueryPageSize(table.getState().pagination.pageSize);
            }}
            isDisabled={!table.getCanNextPage()}
            icon={<AiOutlineArrowRight h={6} w={6} />}
          />
        </Tooltip>
        <Tooltip label={t("lastPageTooltip")}>
          <IconButton
            aria-label="last-page-button"
            onClick={() => {
              table.setPageIndex(table.getPageCount() - 1);
              if (setQueryPageIndex) setQueryPageIndex(table.getPageCount() - 1);
              if (setQueryPageSize) setQueryPageSize(table.getState().pagination.pageSize);
            }}
            isDisabled={!table.getCanNextPage()}
            icon={<AiOutlineDoubleRight h={3} w={3} />}
            ml={4}
          />
        </Tooltip>
      </Flex>
    </Flex>
  );
}

export default DataTableV2;
